import { v4 as uuidv4 } from 'uuid';
import { request } from '../utils';

/**
 * RegistrationService - Handles idempotent registration with V2 API
 * Used for admin manual registrations with parent payment
 */
class RegistrationService {
  constructor() {
    this.isProcessing = false;
    this.pollingAttempts = 0;
    this.maxPollingAttempts = 10;
    // Cache idempotency key for the current registration attempt
    this.currentIdempotencyKey = null;
    this.currentAttemptHash = null;
    this.lastTransactionId = null;
  }

  // Pure function to generate idempotency key
  generateIdempotencyKey = () => uuidv4();

  // Storage keys for session persistence
  getIdempotencyStorageKey = () => 'lpm:admin:idempotency-key';
  getTransactionStorageKey = () => 'lpm:admin:transaction-id';
  getAttemptHashStorageKey = () => 'lpm:admin:attempt-hash';

  /**
   * Persist current registration state to session storage
   * @param {string} idempotencyKey - The idempotency key to store
   * @param {string} attemptHash - The attempt hash to store
   */
  persistRegistrationState = (idempotencyKey, attemptHash) => {
    try {
      if (idempotencyKey) {
        sessionStorage.setItem(this.getIdempotencyStorageKey(), idempotencyKey);
      }
      if (attemptHash) {
        sessionStorage.setItem(this.getAttemptHashStorageKey(), attemptHash);
      }
      return true;
    } catch (error) {
      console.error('Error persisting registration state:', error);
      return false;
    }
  };

  /**
   * Store transaction ID in session storage for status checks
   * @param {string} transactionId - The transaction ID to store
   */
  persistTransactionId = (transactionId) => {
    try {
      sessionStorage.setItem(this.getTransactionStorageKey(), transactionId);
      this.lastTransactionId = transactionId;
      return true;
    } catch (error) {
      console.error('Error persisting transaction ID:', error);
      return false;
    }
  };

  /**
   * Clear stored session data and cached keys
   */
  clearSessionData = () => {
    try {
      sessionStorage.removeItem(this.getIdempotencyStorageKey());
      sessionStorage.removeItem(this.getTransactionStorageKey());
      sessionStorage.removeItem(this.getAttemptHashStorageKey());
      this.currentIdempotencyKey = null;
      this.currentAttemptHash = null;
      this.lastTransactionId = null;
      this.isProcessing = false;
      this.pollingAttempts = 0;
    } catch (error) {
      console.error('Error clearing session data:', error);
    }
  };

  /**
   * Restore session from storage (for page refresh scenarios)
   * @returns {Promise<Object|null>} Status or null if no session
   */
  restoreSession = async () => {
    try {
      const savedTransactionId = sessionStorage.getItem(this.getTransactionStorageKey());
      const savedIdempotencyKey = sessionStorage.getItem(this.getIdempotencyStorageKey());
      const savedAttemptHash = sessionStorage.getItem(this.getAttemptHashStorageKey());

      // Restore cached values
      if (savedIdempotencyKey) {
        this.currentIdempotencyKey = savedIdempotencyKey;
        this.currentAttemptHash = savedAttemptHash;
      }
      if (savedTransactionId) {
        this.lastTransactionId = savedTransactionId;
      }

      // Try to check status using transaction ID first (most reliable)
      if (savedTransactionId) {
        console.log('[RegistrationService] Session restored using transaction ID:', savedTransactionId);
        return await this.checkRegistrationStatusByTransaction(savedTransactionId);
      }

      // Fallback to idempotency key if no transaction ID yet
      if (savedIdempotencyKey) {
        console.log('[RegistrationService] Session restored using idempotency key:', savedIdempotencyKey);
        return await this.checkRegistrationStatus(savedIdempotencyKey);
      }

      return null;
    } catch (error) {
      console.error('Error restoring session:', error);
      return null;
    }
  };

  /**
   * Check registration status using idempotency key or transaction ID
   * @param {string} idempotencyKey - The idempotency key to check
   * @param {string} transactionId - The transaction ID to check (optional)
   * @returns {Promise<Object>} Registration status
   */
  checkRegistrationStatus = async (idempotencyKey, transactionId = null) => {
    try {
      // Use query parameters as recommended (avoid path params for AWS signature)
      const params = new URLSearchParams();
      if (idempotencyKey) params.append('idempotencyKey', idempotencyKey);
      if (transactionId) params.append('transactionId', transactionId);

      const response = await request.get(`/registrations/status?${params.toString()}`);

      console.log('[RegistrationService] Status check completed:', {
        idempotencyKey,
        transactionId,
        success: response.success
      });

      if (response.success) {
        // Store transaction ID if we got one
        if (response.transactionId) {
          this.persistTransactionId(response.transactionId);
        }

        return {
          success: true,
          ...response,
          fromStatusCheck: true
        };
      }

      // Handle error response
      return {
        success: false,
        error: response.error,
        errorType: response.errorType,
        message: response.message,
        canRetry: response.canRetry || false,
        fromStatusCheck: true
      };
    } catch (error) {
      console.error('Error checking registration status:', error);

      // Fallback to POST method if GET fails
      try {
        const postResponse = await request.post('/registrations/status', {
          body: { idempotencyKey, transactionId }
        });
        return postResponse;
      } catch (postError) {
        return {
          success: false,
          error: 'Failed to check registration status',
          canRetry: true
        };
      }
    }
  };

  /**
   * Check registration status by transaction ID only
   * @param {string} transactionId - The transaction ID to check
   * @returns {Promise<Object>} Registration status
   */
  checkRegistrationStatusByTransaction = async (transactionId) => {
    return await this.checkRegistrationStatus(null, transactionId);
  };

  /**
   * Poll registration status with exponential backoff
   * @param {string} idempotencyKey - The key to poll (optional)
   * @param {string} transactionId - The transaction ID to poll (optional)
   * @param {number} maxAttempts - Maximum polling attempts
   * @returns {Promise<Object>} Final registration status
   */
  pollRegistrationStatus = async (idempotencyKey = null, transactionId = null, maxAttempts = 10) => {
    const exponentialBackoff = (attempt) => Math.min(1000 * Math.pow(2, attempt), 10000);

    // Use transaction ID if available, otherwise use idempotency key
    const pollId = transactionId || this.lastTransactionId;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const status = pollId
        ? await this.checkRegistrationStatusByTransaction(pollId)
        : await this.checkRegistrationStatus(idempotencyKey);

      // Return if we have a final status
      if (status.success || (status.success === false && !status.processing)) {
        return status;
      }

      // Wait before next poll with exponential backoff
      const waitTime = exponentialBackoff(attempt);

      console.log('[RegistrationService] Polling registration status:', {
        attempt,
        waitTime,
        idempotencyKey,
        transactionId: pollId
      });

      await new Promise(resolve => setTimeout(resolve, waitTime));
    }

    // Timeout reached
    return {
      success: false,
      error: 'Registration timeout - please check recent registrations',
      canRetry: false,
      timeout: true
    };
  };

  /**
   * Error type handler mapping - updated for V2 error codes
   * Includes key lifecycle management
   */
  errorHandlers = {
    'VALIDATION_ERROR': (error) => ({
      ...error,
      userMessage: error.message || 'Please check your input and try again.',
      showValidationErrors: true,
      canRetry: false,
      shouldClearKey: true  // User needs to fix data
    }),

    'CAPACITY_ERROR': (error) => ({
      ...error,
      userMessage: error.message || 'This class is full. Please select a different class.',
      canRetry: false,
      shouldClearKey: true  // User needs different class
    }),

    'PAYMENT_ERROR': (error) => ({
      ...error,
      userMessage: error.message || 'Payment failed. Please check card details or try a different card.',
      canRetry: true,
      shouldClearKey: false  // Keep key for retry
    }),

    'CONCURRENCY_ERROR': (error) => ({
      ...error,
      userMessage: error.message || 'Registration already in progress. Please wait...',
      canRetry: true,
      shouldPoll: true,
      shouldClearKey: false,  // Keep key for status check
      retryAfter: error.retryAfter || 5000
    }),

    'PROCESSING_FAILED': (error) => ({
      ...error,
      userMessage: error.message || 'An error occurred. Please try again.',
      canRetry: true,
      shouldClearKey: false  // Keep key for retry
    })
  };

  /**
   * Handle error response based on error type
   * @param {Object} errorResponse - Error response from API
   * @param {string} idempotencyKey - The idempotency key used
   * @returns {Promise<Object>} Processed error with user-friendly message
   */
  handleError = async (errorResponse, idempotencyKey) => {
    const handler = this.errorHandlers[errorResponse.errorType] || this.errorHandlers['PROCESSING_FAILED'];
    const processedError = handler(errorResponse);

    // Clear idempotency key if needed (validation or capacity errors)
    if (processedError.shouldClearKey) {
      this.currentIdempotencyKey = null;
      this.currentAttemptHash = null;
      sessionStorage.removeItem(this.getIdempotencyStorageKey());
      sessionStorage.removeItem(this.getAttemptHashStorageKey());
    }

    // Handle DUPLICATE_IN_PROGRESS error specifically
    if (errorResponse.error === 'DUPLICATE_IN_PROGRESS' || processedError.shouldPoll) {
      // Wait and retry with same key to get cached result
      const retryAfter = processedError.retryAfter || errorResponse.retryAfter || 5000;
      await new Promise(resolve => setTimeout(resolve, retryAfter));
      return await this.checkRegistrationStatus(idempotencyKey);
    }

    return processedError;
  };

  /**
   * Submit registration with idempotency
   * @param {Object} formData - Registration form data
   * @returns {Promise<Object>} Registration result
   */
  submitRegistration = async (formData) => {
    // Prevent double submission
    if (this.isProcessing) {
      console.log('[RegistrationService] Registration already in progress');

      // Check status using transaction ID if available
      if (this.lastTransactionId) {
        return await this.checkRegistrationStatusByTransaction(this.lastTransactionId);
      }

      // Or check with cached idempotency key
      if (this.currentIdempotencyKey) {
        return await this.checkRegistrationStatus(this.currentIdempotencyKey);
      }

      return {
        success: false,
        error: 'Registration already in progress',
        processing: true
      };
    }

    // Generate hash of ALL form data to detect any changes
    // ANY change (including card) should generate a new idempotency key
    const attemptHash = JSON.stringify(formData);

    // Only generate new key if:
    // 1. No key exists (first attempt)
    // 2. ANYTHING changed (different card, class, students, products, etc.)
    let idempotencyKey = this.currentIdempotencyKey;

    if (!idempotencyKey || attemptHash !== this.currentAttemptHash) {
      const reason = !idempotencyKey ? 'first_attempt' : 'data_changed';

      // Generate new key for new/changed registration
      idempotencyKey = this.generateIdempotencyKey();
      this.currentIdempotencyKey = idempotencyKey;
      this.currentAttemptHash = attemptHash;

      console.log('[RegistrationService] Generated NEW idempotency key:', {
        idempotencyKey,
        reason,
        timestamp: new Date().toISOString()
      });

      // Persist to session storage
      this.persistRegistrationState(idempotencyKey, attemptHash);
    } else {
      console.log('[RegistrationService] REUSING existing idempotency key:', {
        idempotencyKey,
        reason: 'no_changes_detected',
        timestamp: new Date().toISOString()
      });
    }

    this.isProcessing = true;

    console.log('[RegistrationService] Submitting to API with idempotency key:', {
      idempotencyKey,
      hasCard: !!formData.card,
      cardId: formData.card?.id,
      timestamp: new Date().toISOString()
    });

    try {
      // Submit registration to V2 API
      const response = await request.post('/registrations/v2', {
        body: {
          idempotencyKey,
          ...formData
        }
      });

      console.log('[RegistrationService] V2 API response received:', {
        idempotencyKey,
        success: response.success,
        cached: response.cached,
        transactionId: response.transactionId,
        errorType: response.errorType
      });

      if (response.success) {
        // Store transaction ID for future status checks
        if (response.transactionId) {
          this.persistTransactionId(response.transactionId);
        }

        // Handle cached (duplicate) response
        if (response.cached) {
          // Clear keys on success (even cached)
          this.clearSessionData();
          return {
            ...response,
            userMessage: 'This registration was already processed.',
            isDuplicate: true
          };
        }

        // Clear keys on success for fresh registration
        this.clearSessionData();
        return response;
      }

      // Handle error response
      return await this.handleError(response, idempotencyKey);

    } catch (networkError) {
      // Check if this is an API error response (4xx/5xx with response data)
      if (networkError.response && networkError.response.data) {
        const apiError = networkError.response.data;

        console.log('[RegistrationService] API Error Response:', {
          status: networkError.response.status,
          error: apiError.error,
          errorType: apiError.errorType,
          message: apiError.message
        });

        // Handle the error response through our error handlers
        return await this.handleError(apiError, idempotencyKey);
      }

      // This is an actual network error (no response from server)
      console.error('[RegistrationService] Network error:', networkError);

      return {
        success: false,
        canRetry: true,
        userMessage: 'Network error. Please check your connection and try again.',
        error: 'Network error occurred'
      };
    } finally {
      this.isProcessing = false;
    }
  };

  /**
   * Get current processing state
   * @returns {Object} Current state
   */
  getState = () => ({
    isProcessing: this.isProcessing,
    hasIdempotencyKey: !!this.currentIdempotencyKey,
    idempotencyKey: this.currentIdempotencyKey,
    hasTransactionId: !!this.lastTransactionId,
    transactionId: this.lastTransactionId
  });

  /**
   * Manually start a new registration (clears cached key)
   */
  startNewRegistration = () => {
    this.clearSessionData();
    console.log('[RegistrationService] Started new registration');
  };
}

// Export singleton instance
export default new RegistrationService();
